import { Paragraph, convertMillimetersToTwip } from 'docx'
import { EntryType } from '../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  QuestionSettingCode
} from '../../data/model/questionnaire'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { chain } from '../lodashChain'
import { getQuestionEnabledSettings } from '../questionnaireUtils'
import { createHiddenMarketsLine } from './createHiddenMarketsLine'
import { createResponseLogicText } from './createResponseLogicText'
import { createResponseOptions } from './createResponseOptions'
import { isItemHiddenInMarket } from './marketFilter'
import { Order } from './model'

export const createEntryContentMatrix = (
  entry: DraftQuestionnaireEntry<EntryType.MatrixEntryType>,
  entries: DraftQuestionnaireEntry[],
  allMarkets: MarketOption[] = [],
  marketSurveyId?: string
): Paragraph[] => {
  const entryItem = entry.entryItem
  const parentHiddenInMarkets = entry.hiddenInMarkets ?? []
  const enabledSettings = getQuestionEnabledSettings(entryItem.settingValues)
  const randomisedRowsEnabled = enabledSettings.has(
    QuestionSettingCode.MatrixRandomiseRows
  )

  const rowsParagraphs = chain(entryItem.matrixRows)
    .filter('question.text')
    .sortBy('position')
    .flatMap(({ question, pinned, maskingRules, hiddenInMarkets }, i) => {
      // Master code `i + 1` is kept; rows hidden in the market are skipped.
      if (isItemHiddenInMarket(hiddenInMarkets, marketSurveyId)) return []
      const rowText = `${i + 1}. ${question?.text}`
      const pinnedPostfixIfNeeded =
        pinned && randomisedRowsEnabled ? ' [pinned]' : ''
      const rowParagraph = new Paragraph({
        text: `${rowText}${pinnedPostfixIfNeeded}`,
        children: createResponseLogicText(maskingRules, entries),
        indent: {
          start: convertMillimetersToTwip(6.3)
        }
      })
      const hiddenLine = createHiddenMarketsLine(
        marketSurveyId ? [] : allMarkets,
        hiddenInMarkets,
        parentHiddenInMarkets
      )
      return hiddenLine ? [rowParagraph, hiddenLine] : [rowParagraph]
    })
    .value()

  if (randomisedRowsEnabled) {
    rowsParagraphs.push(new Paragraph(Order.Randomise))
  }

  const columnsParagraphs = [
    new Paragraph({
      text: 'Columns',
      spacing: {
        before: 200
      }
    }),
    ...createResponseOptions(
      entryItem,
      entries,
      allMarkets,
      parentHiddenInMarkets,
      marketSurveyId
    )
  ]

  if (enabledSettings.has(QuestionSettingCode.MatrixRandomiseResponseOptions)) {
    columnsParagraphs.push(new Paragraph(Order.Randomise))
  }

  if (enabledSettings.has(QuestionSettingCode.MatrixFlipOptions)) {
    columnsParagraphs.push(new Paragraph(Order.Reverse))
  }

  return [...rowsParagraphs, ...columnsParagraphs]
}
